import { useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useQuery } from "@apollo/client";

import { getBreadcrumbsBackUrl } from "components/Breadcrumbs/helpers";
import NotFoundPage from "components/error/NotFoundPage";
import PageLoading from "components/loading/PageLoading";
import FullScreenModal from "ds/components/FullScreenModal";
import useErrorHandle from "hooks/useErrorHandle";
import { BlueprintDeployment } from "types/generated";
import { BLUEPRINT_DEPLOYMENT_QUERY } from "views/Account/TemplateDeployment/gql";

import useTemplateVersion from "../useTemplateVersion";
import UpdateDeploymentForm from "./UpdateDeploymentForm";

function isValidLocationState(state: unknown): state is { returnUrl?: string } {
  return (
    state !== null &&
    typeof state === "object" &&
    (!("returnUrl" in state) || typeof (state as { returnUrl?: unknown }).returnUrl === "string")
  );
}

const UpdateDeployment = () => {
  const { versionId, templateId, deploymentId } = useParams<{
    versionId: string;
    templateId: string;
    deploymentId: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    error: versionError,
    loading: versionLoading,
    data: versionData,
  } = useTemplateVersion(templateId, versionId);
  const template = versionData?.blueprintVersionedGroup;

  // Get returnUrl from location state (passed from the table) for instant display
  const locationState = isValidLocationState(location.state) ? location.state : undefined;
  const returnUrl = locationState?.returnUrl;

  // Fetch deployment from API (handles page reloads and ensures fresh data)
  const {
    error: deploymentError,
    loading: deploymentLoading,
    data: deploymentData,
  } = useQuery(BLUEPRINT_DEPLOYMENT_QUERY, {
    variables: {
      blueprintID: templateId!,
      id: deploymentId!,
    },
    skip: !templateId || !deploymentId,
  });

  // Use state deployment for instant display, fall back to fetched data
  const deployment = deploymentData?.blueprintDeployment;

  useErrorHandle(versionError);
  useErrorHandle(deploymentError);

  // Determine back link: use returnUrl from navigation state, or fall back to version-level deployments
  const backLinkUrl =
    returnUrl ||
    (versionId
      ? `/template/${templateId}/version/${versionId}/deployments`
      : `/template/${templateId}/deployments`);
  const backUrl = getBreadcrumbsBackUrl(backLinkUrl, "");

  const handleClose = useCallback(() => {
    navigate(backUrl);
  }, [navigate, backUrl]);

  const loading = versionLoading || deploymentLoading;

  if (loading) {
    return <PageLoading />;
  }

  if (!template?.blueprintVersion || !templateId || !versionId || !deployment) {
    return <NotFoundPage />;
  }

  return (
    <FullScreenModal title={`Update deployment`} onClose={handleClose}>
      <UpdateDeploymentForm
        version={template.blueprintVersion}
        deployment={deployment as BlueprintDeployment}
        templateId={templateId}
        onClose={handleClose}
      />
    </FullScreenModal>
  );
};

export default UpdateDeployment;
